import React, { Fragment, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

// Components

import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { normalizeAddress } from '@utils/normalize/normalizeAddress';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';

// Constants

import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';



const OtherMailingAddresses = props => {
    const {
        areOtherMailingAddressesValid,
        changeData,
        data,
        defaultMailingAddress,
        defaultMailingAddressId,
        handleAdding,
        handleDeleting,
        handleEditing,
        hasSomeoneWithAnotherMailingAddress,
        householdMembers,
        isFormSubmitted,
        isEligiblyForSave,
        otherAddresses,
        primaryContactMailingAddress,
        parentForm,
        toggleMailingAddressesValidation,
        underFormControl
    } = props;

    const areOtherAddressesProvided = useMemo(
        () => {
            let result = true;

            householdMembers.forEach((hhm, hhmIndex) => {
                if(hhmIndex !== 0 && hhm.hasAnotherMailingAddress && hhm.mailingAddressId === defaultMailingAddressId) {
                    result = false;
                }
            });

            const areOtherAddresses = householdMembers.some(hhm => hhm.hasAnotherMailingAddress);

            return areOtherAddresses && result;
        },
        [ householdMembers.map(hhm => hhm.hasAnotherMailingAddress) ]
    );

    const isValid = useMemo(
        () => {
            switch(hasSomeoneWithAnotherMailingAddress === false) {
                case true:
                    return true;
                case false:
                case null:
                    return areOtherAddressesProvided;
            }
        },
        [ areOtherAddressesProvided, hasSomeoneWithAnotherMailingAddress ]
    );

    useEffect(
        () => {
            toggleMailingAddressesValidation(isValid);
        },
        [ isValid ]
    );

    const onFieldChange_noneOfAboveMailingAddresses = (...args) => {

        const [ newData ] = args;

        const updateIndividualValues = hhm => {
            return {
                ...hhm,
                hasAnotherMailingAddress: false,
                householdContact: {
                    ...hhm.householdContact,
                    mailingAddress: defaultMailingAddress
                },
                mailingAddressId: defaultMailingAddressId
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: 'mailingAddress'
            }
        );

        changeData(syncedData);
    };

    const onFieldChange_mailingAddress = (...args) => {

        const { indexInArray: [ hhmIndex, newAddressId ], value } = parseFieldChangeArgs(args);

        const newState = { ...data };

        if(value) {
            (hhm => {
                hhm.mailingAddressId = newAddressId;
                hhm.householdContact.mailingAddress = otherAddresses.find(a => a.addressId === newAddressId);
            })(newState.taxHousehold.householdMember[hhmIndex]);
        } else {
            (hhm => {
                hhm.mailingAddressId = defaultMailingAddressId;
                hhm.householdContact.mailingAddress = defaultMailingAddress;
            })(newState.taxHousehold.householdMember[hhmIndex]);
        }

        changeData(newState);
    };

    const onFieldChange_otherMailingAddress = (...args) => {

        let { indexInArray: hhmIndex, newData, value } = parseFieldChangeArgs(args);

        const newState = { ...data };

        newState.taxHousehold.householdMember[hhmIndex] = newData;

        if(!value) {
            newState.taxHousehold.householdMember[hhmIndex].householdContact.mailingAddress = defaultMailingAddress;
            newState.taxHousehold.householdMember[hhmIndex].mailingAddressId = defaultMailingAddressId;
        }

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'mailingAddress' }
        );

        changeData(syncedData);
    };

    const getHouseholdMembers = householdMembers => {
        const members = [];

        householdMembers.forEach((member, i) => {
            const {
                hasAnotherMailingAddress,
                mailingAddressId,
                name
            } = member;

            if (i !== 0) {
                members.push(
                    <li key = { `${ normalizeNameOfPerson(name) }_mailing_address_${ i }` } >
                        <UniversalInput
                            label = { {
                                text: 'common.input.label.non_internationalized_text',
                                labelTextReplacements: {
                                    text: normalizeNameOfPerson(name)
                                }
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                classes: [],
                                name: 'hasAnotherMailingAddress',
                                checked: hasAnotherMailingAddress,
                                value: hasAnotherMailingAddress,
                                currentData: member,
                                fields: [],
                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange_otherMailingAddress
                            } }
                            indexInArray = { i }
                            { ...{
                                parentForm,
                                underFormControl
                            } }
                        />
                        {
                            hasAnotherMailingAddress &&

                                <Fragment>
                                    {
                                        otherAddresses.length === 0 &&

                                        <FormattedMessage
                                            id = 'ssap.start.addresses.provide_address'
                                            tagName = 'p'
                                        />
                                    }

                                    {
                                        otherAddresses.map((address, idx) => {

                                            const {
                                                indexInOtherAddresses,
                                                addressId: id
                                            } = address;

                                            return (
                                                <div
                                                    className = 'grid-row address_l2'
                                                    key = { `${ address.addressLine1 }_mailing_address_${ idx }` }
                                                >
                                                    <UniversalInput
                                                        label = { {
                                                            text: 'common.input.label.non_internationalized_text',
                                                            labelTextReplacements: {
                                                                text: normalizeAddress(address)
                                                            }
                                                        } }
                                                        inputData = { {
                                                            isInputRequired: false,
                                                            isValidationErrorMessageVisible: false,
                                                            type: INPUT_TYPES.CHECKBOX,
                                                            name: 'addressId',
                                                            classes: [ '' ],
                                                            checked: address.addressId === mailingAddressId,
                                                            value: address.addressId,
                                                            currentData: { mailingAddressId: address.addressId },
                                                            fields: [],
                                                            valueToPass: 'hasAnotherMailingAddress'
                                                        } }
                                                        inputActions = { {
                                                            onFieldChange: onFieldChange_mailingAddress
                                                        } }
                                                        indexInArray = { [ i, address.addressId ] }
                                                        { ...{
                                                            parentForm,
                                                            underFormControl
                                                        } }
                                                    />
                                                    <div className='grid-row margin-b-10' >
                                                        <button
                                                            className = 'usa-button usa-button--outline gi-button gi-button__outline gi-button--small'
                                                            onClick = { handleDeleting }
                                                            name = { indexInOtherAddresses }
                                                            data-id = { id }
                                                        >
                                                            <FormattedMessage id = 'button.delete' />
                                                        </button>
                                                        <button
                                                            className = 'usa-button usa-button--outline gi-button gi-button__outline gi-button--small'
                                                            onClick = { handleEditing }
                                                            name = { indexInOtherAddresses }
                                                        >
                                                            <FormattedMessage id = 'button.edit' />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    }

                                    <button
                                        onClick = { handleAdding }
                                        className = 'usa-button usa-button--outline gi-button gi-button__outline gi-button--small'
                                    >
                                        <FormattedMessage id = 'button.add_address' />
                                    </button>
                                </Fragment>
                        }
                    </li>
                );
            }
        });

        members.push(
            <li key = 'hasSomeoneWithAnotherMailingAddress'>
                <UniversalInput
                    label = { {
                        text: 'ssap.input.label.none_of_above'
                    } }
                    inputData = { {
                        isInputRequired: false,
                        type: INPUT_TYPES.CHECKBOX,
                        name: 'hasSomeoneWithAnotherMailingAddress',
                        classes:[],
                        checked: hasSomeoneWithAnotherMailingAddress === false,
                        value: hasSomeoneWithAnotherMailingAddress === false,
                        currentData: cloneObject(data),
                        fields: [ 'taxHousehold', 'thCommonParams' ],
                        reversedValue: true
                    } }
                    inputActions = { {
                        onFieldChange: onFieldChange_noneOfAboveMailingAddresses
                    } }
                    { ...{
                        parentForm,
                        underFormControl
                    } }
                />
            </li>
        );

        return members;
    };

    return (
        <fieldset className = 'usa-fieldset' >
            <legend className = 'required-true' data-testid = 'have_the_same_mailing_address_legend' >
                <FormattedMessage
                    id = 'ssap.input.legend.have_the_same_mailing_address'
                    values = { {
                        primaryContactMailingAddress: (
                            <strong>
                                { normalizeAddress(primaryContactMailingAddress) }
                            </strong>
                        )
                    } }
                />
            </legend>
            <ValidationErrorMessage
                isVisible = { isFormSubmitted && !isEligiblyForSave && !areOtherMailingAddressesValid }
                messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                errorClasses = { [ 'margin-l-0' ] }
            />
            <ul className = 'usa-list usa-list--unstyled' >
                {
                    getHouseholdMembers(householdMembers)
                }
            </ul>
        </fieldset>
    );
};

export default OtherMailingAddresses;